import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Search, Instagram, Phone, Cake } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { SectionHeader, Empty } from "@/components/ui-bits";
import { listClients, createClient } from "@/lib/api";
import { formatDate } from "@/lib/format";

const blank = { name: "", phone: "", instagram: "", birthday: "", notes: "" };

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const load = (s = "") => listClients(s).then(setClients).catch(() => setClients([]));

  useEffect(() => { load(); }, []);

  const onSearch = (e) => {
    const v = e.target.value;
    setSearch(v);
    load(v);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Nome é obrigatório");
      return;
    }
    setSaving(true);
    try {
      await createClient({
        ...form,
        instagram: form.instagram.replace(/^@/, "") || null,
        birthday: form.birthday || null,
      });
      toast.success("Cliente cadastrada");
      setForm(blank);
      setOpen(false);
      load(search);
    } catch (err) {
      toast.error("Erro ao salvar cliente");
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      <SectionHeader
        eyebrow="Cadastro"
        title="Clientes"
        description="Sua agenda de pessoas marcadas — cada uma com sua história tatuada."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="open-create-client" className="rounded-full bg-[#7B8B6F] hover:bg-[#647259] text-white px-6 py-5 gap-2">
                <Plus className="w-4 h-4" /> Nova Cliente
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#FDFCF7] border-[#E8E4D9]">
              <DialogHeader>
                <DialogTitle className="font-serif-display text-3xl text-[#2E332C]">Nova Cliente</DialogTitle>
                <DialogDescription>Cadastre os dados básicos. Você pode adicionar tatuagens, números e lembretes depois.</DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4 mt-2">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5C6658]">Nome completo*</Label>
                  <Input data-testid="client-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 bg-white" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#5C6658]">Telefone</Label>
                    <Input data-testid="client-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2 bg-white" placeholder="(11) 99999-9999" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#5C6658]">Instagram</Label>
                    <Input data-testid="client-instagram-input" value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} className="mt-2 bg-white" placeholder="@usuario" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5C6658]">Data de aniversário</Label>
                  <Input data-testid="client-birthday-input" type="date" value={form.birthday} onChange={(e) => setForm({ ...form, birthday: e.target.value })} className="mt-2 bg-white" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5C6658]">Notas</Label>
                  <Textarea data-testid="client-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-2 bg-white" rows={3} />
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="submit-client" disabled={saving} className="rounded-full bg-[#7B8B6F] hover:bg-[#647259] text-white px-8">
                    {saving ? "Salvando…" : "Salvar"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="relative mb-8 max-w-md">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6658]" />
        <Input
          data-testid="client-search"
          value={search}
          onChange={onSearch}
          placeholder="Buscar por nome, telefone ou @"
          className="pl-11 bg-white border-[#E8E4D9] rounded-full py-5"
        />
      </div>

      {clients.length === 0 ? (
        <Empty title="Nenhuma cliente cadastrada" hint="Comece adicionando sua primeira cliente." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="clients-grid">
          {clients.map((c) => (
            <Link key={c.id} to={`/clientes/${c.id}`} className="elegant-card p-7 hover:-translate-y-1 transition-all block" data-testid={`client-card-${c.id}`}>
              <h3 className="font-serif-display text-2xl text-[#2E332C] leading-tight">{c.name}</h3>
              <div className="mt-4 space-y-1.5 text-sm text-[#5C6658]">
                {c.instagram && (
                  <p className="flex items-center gap-2"><Instagram className="w-3.5 h-3.5" /> @{c.instagram.replace(/^@/, "")}</p>
                )}
                {c.phone && (
                  <p className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" /> {c.phone}</p>
                )}
                {c.birthday && (
                  <p className="flex items-center gap-2"><Cake className="w-3.5 h-3.5" /> {formatDate(c.birthday)}</p>
                )}
              </div>
              <div className="flex gap-3 mt-5 pt-5 border-t border-[#E8E4D9]">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#5C6658]">Tattoos</p>
                  <p className="font-serif-display text-2xl text-[#7B8B6F]">{c.total_tattoos ?? 0}</p>
                </div>
                <div className="ml-6">
                  <p className="text-[10px] uppercase tracking-wider text-[#5C6658]">Nºs</p>
                  <p className="font-serif-display text-2xl text-[#C4A77D]">{c.total_lucky_numbers ?? 0}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </motion.div>
  );
}
